import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  alpha,
  Chip,
  Stack,
  CircularProgress,
  Grid,
  Paper,
  LinearProgress
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Assessment as AssessmentIcon,
  CompareArrows as CompareArrowsIcon
} from '@mui/icons-material';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Transaction } from '../../types';
import { processMonthlyData, formatCurrency, getTrendColor, MonthlyData } from '../../utils/chartUtils';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface TrendAnalysisChartProps {
  transactions: Transaction[];
  loading?: boolean;
  analysisType?: 'monthly' | 'quarterly';
}

const TrendAnalysisChart: React.FC<TrendAnalysisChartProps> = ({
  transactions,
  loading = false,
  analysisType = 'monthly'
}) => {
  const theme = useTheme();

  const monthlyData = useMemo(() => {
    return processMonthlyData(transactions, analysisType === 'quarterly' ? 4 : 12);
  }, [transactions, analysisType]);

  const chartData = useMemo(() => {
    return {
      labels: monthlyData.map((d: MonthlyData) => d.month),
      datasets: [
        {
          label: 'Income',
          data: monthlyData.map((d: MonthlyData) => d.income),
          backgroundColor: alpha(theme.palette.success.main, 0.7),
          borderColor: theme.palette.success.main,
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        },
        {
          label: 'Expenses',
          data: monthlyData.map((d: MonthlyData) => d.expenses),
          backgroundColor: alpha(theme.palette.error.main, 0.7),
          borderColor: theme.palette.error.main,
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        },
        {
          label: 'Savings',
          data: monthlyData.map((d: MonthlyData) => d.savings),
          backgroundColor: alpha(theme.palette.info.main, 0.7),
          borderColor: theme.palette.info.main,
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        },
        {
          label: 'Net Income',
          data: monthlyData.map((d: MonthlyData) => d.net),
          backgroundColor: monthlyData.map((d: MonthlyData) => alpha(d.net >= 0 ? theme.palette.success.main : theme.palette.error.main, 0.5)),
          borderColor: monthlyData.map((d: MonthlyData) => d.net >= 0 ? theme.palette.success.main : theme.palette.error.main),
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        }
      ]
    };
  }, [monthlyData, theme]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          pointStyle: 'rect',
          padding: 20,
          color: theme.palette.text.primary,
          font: {
            size: 12,
            family: theme.typography.fontFamily
          }
        }
      },
      tooltip: {
        backgroundColor: alpha(theme.palette.background.paper, 0.95),
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || '';
            const value = formatCurrency(context.parsed.y);
            return `${label}: ${value}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            size: 11,
            family: theme.typography.fontFamily
          }
        }
      },
      y: {
        grid: {
          color: alpha(theme.palette.divider, 0.1)
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            size: 11,
            family: theme.typography.fontFamily
          },
          callback: (value: any) => formatCurrency(value)
        }
      }
    },
    interaction: {
      mode: 'index' as const,
      intersect: false
    }
  }), [theme]);

  // Calculate trend insights
  const trendInsights = useMemo(() => {
    if (monthlyData.length < 2) return null;

    const recent = monthlyData.slice(-3); // Last 3 months
    const previous = monthlyData.slice(-6, -3); // Previous 3 months

    const recentAvgExpenses = recent.reduce((sum: number, d: MonthlyData) => sum + d.expenses, 0) / recent.length;
    const previousAvgExpenses = previous.length > 0 ? previous.reduce((sum: number, d: MonthlyData) => sum + d.expenses, 0) / previous.length : 0;
    
    const recentAvgIncome = recent.reduce((sum: number, d: MonthlyData) => sum + d.income, 0) / recent.length;
    const previousAvgIncome = previous.length > 0 ? previous.reduce((sum: number, d: MonthlyData) => sum + d.income, 0) / previous.length : 0;

    const expenseChange = previousAvgExpenses > 0 ? ((recentAvgExpenses - previousAvgExpenses) / previousAvgExpenses) * 100 : 0;
    const incomeChange = previousAvgIncome > 0 ? ((recentAvgIncome - previousAvgIncome) / previousAvgIncome) * 100 : 0;

    const expenseTrend = expenseChange > 5 ? 'increasing' : expenseChange < -5 ? 'decreasing' : 'stable' as const;
    const incomeTrend = incomeChange > 5 ? 'increasing' : incomeChange < -5 ? 'decreasing' : 'stable' as const;

    // Calculate savings rate
    const recentSavingsRate = recentAvgIncome > 0 ? (recent.reduce((sum: number, d: MonthlyData) => sum + d.savings, 0) / recent.length / recentAvgIncome) * 100 : 0;
    const previousSavingsRate = previousAvgIncome > 0 && previous.length > 0 ? (previous.reduce((sum: number, d: MonthlyData) => sum + d.savings, 0) / previous.length / previousAvgIncome) * 100 : 0;

    // Best and worst months
    const bestMonth = monthlyData.reduce((best: MonthlyData, current: MonthlyData) => current.net > best.net ? current : best, monthlyData[0]);
    const worstMonth = monthlyData.reduce((worst: MonthlyData, current: MonthlyData) => current.net < worst.net ? current : worst, monthlyData[0]);

    return {
      expenseChange,
      incomeChange,
      expenseTrend,
      incomeTrend,
      recentAvgExpenses,
      previousAvgExpenses,
      recentAvgIncome,
      previousAvgIncome,
      recentSavingsRate,
      previousSavingsRate,
      bestMonth,
      worstMonth
    } as const;
  }, [monthlyData]);

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" height="400px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)}, ${alpha(theme.palette.background.paper, 0.4)})`,
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight="600">
          Trend Analysis
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {analysisType === 'quarterly' ? 'Quarterly' : 'Monthly'} financial trends and patterns
        </Typography>

        {/* Trend Insights */}
        {trendInsights && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  background: alpha(getTrendColor(trendInsights.expenseTrend, theme), 0.05),
                  border: `1px solid ${alpha(getTrendColor(trendInsights.expenseTrend, theme), 0.1)}`,
                  borderRadius: 2
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  {trendInsights.expenseTrend === 'increasing' && <TrendingUpIcon fontSize="small" />}
                  {trendInsights.expenseTrend === 'decreasing' && <TrendingDownIcon fontSize="small" />}
                  {trendInsights.expenseTrend === 'stable' && <TrendingFlatIcon fontSize="small" />}
                  <Typography variant="body2" color="text.secondary">
                    Expense Trend
                  </Typography>
                </Stack>
                <Typography variant="h6" fontWeight="600" sx={{ color: getTrendColor(trendInsights.expenseTrend, theme) }}>
                  {trendInsights.expenseChange > 0 ? '+' : ''}{trendInsights.expenseChange.toFixed(1)}%
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  background: alpha(getTrendColor(trendInsights.incomeTrend, theme), 0.05),
                  border: `1px solid ${alpha(getTrendColor(trendInsights.incomeTrend, theme), 0.1)}`,
                  borderRadius: 2
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  {trendInsights.incomeTrend === 'increasing' && <TrendingUpIcon fontSize="small" />}
                  {trendInsights.incomeTrend === 'decreasing' && <TrendingDownIcon fontSize="small" />}
                  {trendInsights.incomeTrend === 'stable' && <TrendingFlatIcon fontSize="small" />}
                  <Typography variant="body2" color="text.secondary">
                    Income Trend
                  </Typography>
                </Stack>
                <Typography variant="h6" fontWeight="600" sx={{ color: getTrendColor(trendInsights.incomeTrend, theme) }}>
                  {trendInsights.incomeChange > 0 ? '+' : ''}{trendInsights.incomeChange.toFixed(1)}%
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  background: alpha(theme.palette.info.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                  borderRadius: 2
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Savings Rate
                </Typography>
                <Typography variant="h6" fontWeight="600" color="info.main">
                  {trendInsights.recentSavingsRate.toFixed(1)}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(trendInsights.recentSavingsRate, 100)}
                  sx={{ mt: 1, height: 6, borderRadius: 3 }}
                />
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  background: alpha(theme.palette.warning.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
                  borderRadius: 2
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Best Month
                </Typography>
                <Typography variant="body2" fontWeight="600" color="warning.main">
                  {trendInsights.bestMonth.month}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatCurrency(trendInsights.bestMonth.net)} net
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Chart */}
        <Box sx={{ height: '350px', position: 'relative' }}>
          <Bar data={chartData} options={chartOptions} />
        </Box>

        {/* Monthly Comparison */}
        {monthlyData.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Recent Performance
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Paper elevation={0} sx={{ p: 2, backgroundColor: alpha(theme.palette.success.main, 0.05) }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Average Monthly Income
                  </Typography>
                  <Typography variant="h6" fontWeight="600" color="success.main">
                    {formatCurrency(monthlyData.reduce((sum: number, d: MonthlyData) => sum + d.income, 0) / monthlyData.length)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper elevation={0} sx={{ p: 2, backgroundColor: alpha(theme.palette.error.main, 0.05) }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Average Monthly Expenses
                  </Typography>
                  <Typography variant="h6" fontWeight="600" color="error.main">
                    {formatCurrency(monthlyData.reduce((sum: number, d: MonthlyData) => sum + d.expenses, 0) / monthlyData.length)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Trend Chips */}
        {trendInsights && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                icon={<CompareArrowsIcon />}
                label={`Income is ${trendInsights.incomeTrend}`}
                color={trendInsights.incomeTrend === 'increasing' ? 'success' : trendInsights.incomeTrend === 'decreasing' ? 'error' : 'info'}
                variant="outlined"
                size="small"
              />
              <Chip
                icon={<AssessmentIcon />}
                label={`Expenses are ${trendInsights.expenseTrend}`}
                color={trendInsights.expenseTrend === 'increasing' ? 'error' : trendInsights.expenseTrend === 'decreasing' ? 'success' : 'info'}
                variant="outlined"
                size="small"
              />
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TrendAnalysisChart; 